
import { object } from "../helpers/object";
import { debug } from "../helpers/debug";
import { utils } from "../helpers/utils";
import { responsive } from "../helpers/responsive";

/*------------------------------------*\
  #ACCORDION

  Class gestion des element [data-js-component="accordion"]

  [data-js-component="accordion"]
  [data-js-accordion-close="{{bool|string}}"] 
    @params false(default) | true | mobile | desktop
    comportement de fermeture automatique
  [data-js-accordion-disable="{{bool|string}}"] 
    @params false(default) | mobile | desktop
    component inactif en fonction du mediaquery
  [data-js-accordion-group="{{string}}"]
    liaison de group entre différents accordion

  [data-js-trigger="accordion--toggler"]
    trigger d'ouverture|fermeture de l'accordeon

  [data-js-accordion-expanded="true"] 
    etat d'ouverture de l'élement cible de l'accordeon

  structure: 
  | WRAPPER
  | [data-js-component="accordion"]
  | [data-js-accordion-close="{{bool|string}}"]
  | [data-js-accordion-disable="{{bool|string}}"] 
  | [data-js-accordion-group="{{string}}"]

    | TOGGLER
    | [data-js-trigger="accordion--toggler"]
    | [aria-controls="{{id_target}}"]
    | aria-expanded="{{bool}}"

    | TARGET
    | #id_target
    | [data-js-accordion-expanded="{{bool}}"]
\*------------------------------------*/
class Accordion {

  constructor(module){
    this.MODULE = module;
    this.DOM_MODULE = object.toArray(document.querySelectorAll(this.MODULE));
  }
  /**
   * INITIALISATION
   * @returns 
   */
  init = () => {
    if (!this.DOM_MODULE.length) return;

    // DEFAULT EVENT
    document.addEventListener('click', this.clickHandler);
    // INIT RESPONSIVE
    this.responsiveHandler();

    setTimeout(() => {
      this.initParams();
    }, 1000)

    if(debug.DEBUG) console.log('async/accordion.js');
  }

  /**
   * CLICK HANDLER
   * 
   * Gestionnaire des event de la barre de navigation
   * @param {event} event 
   * @returns 
   */
  clickHandler = (event) => {
    if (event.target.matches('[data-js-accordion="toggler"]')) {
      event.stopPropagation();
      event.preventDefault();
      this.toggle(event.target.closest('[data-js-component="accordion"]'));
    }
  }
  /**
   * RESPONSIVE
   */
  responsiveHandler = () => {
    document.addEventListener('RESPONSIVE_HAS_CHANGED', (event) => {
      // this.responsiveButtonsHandler();
      this.initParams();
    })
  };
  /**
   * CLOSE GROUP
   * 
   * Fermeture des accodions du même groupe
   * @param {string} group 
   */
  closeGroup = (group) => {
    this.DOM_MODULE.forEach(element => {
      const dataGroup = element.getAttribute('data-js-accordion-group');
      if (!dataGroup) return;
      if (dataGroup === group) {
        this.toggle(element, false);
      }
    });
  }
    
  /**
   * TOGGLE
   * 
   * Ouvertu|fermeture du l'accodion 
   * 
   * @param {HTMLElement} element 
   * @param {boolean|string} value 
   * @returns 
   */
  toggle = (element, value, closeGroup = true) => {
    const toggler = element.querySelector('[data-js-accordion="toggler"]');
    if (!toggler) return;
  
    if (toggler.nodeName === 'SPAN') value = true;
  
    const target = document.getElementById(this.getTogglerId(toggler));
    if (!target) return;
  
    const expanded = value !== undefined ? value : !utils.dataBool(toggler.getAttribute('aria-expanded'));
  
    if (expanded && closeGroup && element.matches('[data-js-accordion-group]')) {
      this.closeGroup(element.getAttribute('data-js-accordion-group'));
    }
  
    const ariaAttr = toggler.matches('[aria-expanded]') ? 'aria-expanded' : 'data-js-aria-expanded';
    toggler.setAttribute(ariaAttr, expanded);
  
    target.setAttribute('data-js-accordion-expanded', expanded);
    target.setAttribute('aria-hidden', !expanded);
    if (expanded) {
      target.removeAttribute('inert');
    } else {
      target.setAttribute('inert', '');
    }
  
    this.focusHandler(target);
  }
  
  /**
   * INIT PARAMS
   */
  initParams = () => {
    this.DOM_MODULE.forEach(element => {
      this.disableOnResponsive(element);
      this.closeOnResponsive(element);
      this.setHeightVariable(element);
    });
  }
  /**
   * FOCUS HANDLER
   * 
   * Gestionnaire des focus des liens
   */
  focusHandler = (target) => {
    const elements = target.querySelectorAll('a, button');
    elements.forEach(element => {
      const parent = element.closest('[data-js-component="accordion"]');
      if (!parent) return;
      if (target.getAttribute('aria-hidden') === 'true') {
        element.setAttribute('tabindex', -1);
      } else {
        element.removeAttribute('tabindex');
      }
    });
  }
  /**
   * Retrieves the ID of the element that the given element controls.
   *
   * This function checks the 'aria-controls' attribute and the 'data-js-aria-controls' attribute
   * of the provided element. It returns the value of the first attribute that is found, or `false`
   * if neither attribute is present.
   *
   * @param {HTMLElement} element - The element from which to retrieve the ID.
   * @returns {string|boolean} The ID of the controlled element, or `false` if not found.
   */
  getTogglerId = (element) => {
    const ariaControls = element.getAttribute('aria-controls');
    const dataJsAriaControls = element.getAttribute('data-js-aria-controls');
    return ariaControls || dataJsAriaControls || false;
  }
  /**
   * DISABLE ON RESPONSIVE
   * 
   * enable|disable de l'accordion en
   * fonction de l'attribute
   * [data-js-accordion-disable]
   * 
   * @param {HTMLElement} element 
   * @returns 
   */
  disableOnResponsive = (element) => {
    const dataDisable = element.getAttribute('data-js-accordion-disable') ?? 'false';
    const toggler = element.querySelector('[data-js-accordion="toggler"]');
    if (!toggler) return;
    const target = document.getElementById(this.getTogglerId(toggler));
    if (!target) return;
  
    const isResponsiveLg = responsive.is('lg');
    const shouldDisable = (dataDisable === 'mobile' && !isResponsiveLg) || (dataDisable === 'desktop' && isResponsiveLg);
  
    if (dataDisable === 'false' || !shouldDisable) {
      this.togglerConverter(toggler, 'button');
    } else {
      this.togglerConverter(toggler, 'span');
      this.toggle(element, true);
    }
  }

  /**
   * CLOSE ON RESPONSIVE
   * 
   * Ferme l'accordéon en fonction de l'attribut
   * [data-js-accordion-close] et du mediaquery
   * 
   * @param {HTMLElement} element 
   */
  closeOnResponsive = (element) => {
    const dataClose = element.getAttribute('data-js-accordion-close');
    const toggler = element.querySelector('[data-js-accordion="toggler"]');
    if (!toggler) return;

    const target = document.getElementById(this.getTogglerId(toggler));
    if (!target) return;

    const shouldClose = dataClose === 'true' ? true : dataClose === 'false' ? false : dataClose;

    if (typeof shouldClose === 'boolean') {
      this.toggle(element, !shouldClose);
    } else {
      const isDesktop = responsive.is('lg');
      const shouldToggle = (isDesktop && shouldClose === 'desktop') || (!isDesktop && shouldClose === 'mobile');
      this.toggle(element, !shouldToggle, false);
    }
  }

  /**
   * Sets the CSS variable `--accordion-target-height` on the given element based on the height of its target element.
   * 
   * @param {HTMLElement} element - The accordion element containing the toggler and target elements.
   * 
   * The function performs the following steps:
   * 1. Finds the toggler element within the given element.
   * 2. Finds the target element using the ID derived from the toggler.
   * 3. Removes any existing `--accordion-target-height` property from the element.
   * 4. Sets the target element's height to 'auto' to calculate its natural height.
   * 5. Retrieves the height of the target element's first child.
   * 6. Removes the inline style from the target element.
   * 7. Sets the `--accordion-target-height` property on the element to the calculated height if the toggler is a button.
   * 8. Removes the inline style from the element if the toggler is not a button.
   */
  setHeightVariable = (element) => {
    // toggler element
    const toggler = element.querySelector('[data-js-accordion="toggler"]');
    if (!toggler) return;
    // target element
    const target = document.getElementById(this.getTogglerId(toggler));
    if (!target) return;

    element.style.removeProperty('--accordion-target-height');
    target.style.height = 'auto';
    
    let targetHeight = target.firstElementChild.offsetHeight;
    target.removeAttribute('style');
    targetHeight = targetHeight === 0 ? "auto" : `${targetHeight}px`;
    
    if (toggler.nodeName === 'BUTTON') {
      element.style.setProperty('--accordion-target-height', targetHeight);
    } else {
      element.removeAttribute('style');
    }

  }
  /**
   * LINK BUTTON CONVERTER
   * 
   * Conversion d'un link en button 
   * et inversement 
   * 
   * @param {HTMLElement} elementOld 
   * @param {string} type 
   */
  togglerConverter = (elementOld, type) => {
    const accordion = elementOld.closest('[data-js-component="accordion"]');
    const elementNew = document.createElement(type);
    elementNew.innerHTML = elementOld.innerHTML;
  
    [...elementOld.attributes].forEach(({ name, value }) => {
      elementNew.setAttribute(name, value);
    });
  
    if (type === 'button') {
      if (!elementNew.querySelector('i')) {
        const chevron = document.createElement('i');
        chevron.setAttribute('aria-hidden', true);
        chevron.classList.add('c-icon--chevron-up', 'u-hidden-from--lg');
        elementNew.appendChild(chevron);
      }
  
      elementNew.setAttribute('aria-expanded', false);
      elementNew.classList.add('c-accordion__toggler');
      utils.renameAttribute(elementNew, 'data-js-aria-expanded', 'aria-expanded');
      utils.renameAttribute(elementNew, 'data-js-aria-controls', 'aria-controls');
      accordion.classList.add('c-accordion');
    } else {
      elementNew.classList.remove('c-accordion__toggler');
      utils.renameAttribute(elementNew, 'aria-expanded', 'data-js-aria-expanded');
      utils.renameAttribute(elementNew, 'aria-controls', 'data-js-aria-controls');
      accordion.classList.remove('c-accordion');
    }
  
    elementOld.replaceWith(elementNew);
  };

}
export default new Accordion('[data-js-component="accordion"]');